import { useQueryClient, useMutation } from '@tanstack/react-query';
import { operations } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';
import useDataDog from '@/reporting/DataDog';
import uploadDocumentViaS3 from './uploadDocumentViaS3';
import { REMITTANCE_ENVELOPES_QUERY_KEY } from './useRemittanceEnvelopes';

type CreateSignedDocumentBody =
  operations['createRemittanceSignedDocument']['requestBody']['content']['application/json'];
type CreateSignedDocumentResponse =
  operations['createRemittanceSignedDocument']['responses'][201]['content']['application/json'];
type CompleteSignedDocumentBody =
  operations['completeRemittanceSignedDocument']['requestBody']['content']['application/json'];

export interface UploadSignedRemittanceDocumentParams {
  envelopeUuid: string;
  file: File;
}

const useUploadSignedRemittanceDocument = () => {
  const { addError } = useDataDog();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['useUploadSignedRemittanceDocument'],
    mutationFn: async ({ envelopeUuid, file }: UploadSignedRemittanceDocumentParams) => {
      // Uploads directly to S3 — bypasses the gateway/apiClient, and must NOT send the Bearer
      // auth header: the presigned URL's own signature is the credential here, and the S3
      // Object Lock config requires the checksum header signed into the URL (see
      // documents-s3.service.ts in supplier-integration-api for why all three headers matter).
      await uploadDocumentViaS3(
        file,
        async ({ fileName, contentType, contentSha256 }) => {
          const createResponse = await apiClient.post(
            `api/supplier-experience/envelopes/${envelopeUuid}/signed-document`,
            { json: { fileName, contentType, contentSha256 } satisfies CreateSignedDocumentBody }
          );
          const { data } = await createResponse.json<CreateSignedDocumentResponse>();
          return data;
        },
        async (documentUuid, size) => {
          await apiClient.post(
            `api/supplier-experience/envelopes/${envelopeUuid}/signed-document/${documentUuid}/complete`,
            { json: { size } satisfies CompleteSignedDocumentBody }
          );
        }
      );

      return { envelopeUuid };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REMITTANCE_ENVELOPES_QUERY_KEY });
    },
    onError: (error, variables) => {
      addError('useUploadSignedRemittanceDocument', {
        error,
        envelopeUuid: variables.envelopeUuid,
      });
    },
  });
};

export default useUploadSignedRemittanceDocument;
